import { auth } from '../firebase/config';

/**
 * Client for the storage-gateway service, which proxies Azure Blob access for
 * employees so the browser never holds a credential that could delete data.
 * Video is the exception: videoUrl() returns a read-only SAS URL so the player
 * can issue range requests straight to Azure.
 */
class StorageGatewayService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';

    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.baseUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8007'  // Local storage-gateway API
      : `https://storage-gateway-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Get auth headers with Firebase ID token
   */
  async _getAuthHeaders(extra = {}) {
    const headers = { ...extra };
    const user = auth.currentUser;
    if (user) {
      const idToken = await user.getIdToken();
      headers['X-Firebase-Token'] = idToken;
    }
    return headers;
  }

  async _request(path, { method = 'GET', body, contentType, raw = false } = {}) {
    const headers = await this._getAuthHeaders(contentType ? { 'Content-Type': contentType } : {});
    const response = await fetch(`${this.baseUrl}${path}`, { method, headers, body });

    if (raw) {
      if (!response.ok) {
        throw new Error(await this._errorMessage(response));
      }
      return response;
    }

    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Request failed with status ${response.status}`);
    }
    return data;
  }

  async _errorMessage(response) {
    const data = await response.json().catch(() => null);
    return data?.error || `Request failed with status ${response.status}`;
  }

  _blobPath(container, path) {
    return `/api/storage/blob?container=${encodeURIComponent(container)}&path=${encodeURIComponent(path)}`;
  }

  async listContainers() {
    const data = await this._request('/api/storage/containers');
    return data.containers;
  }

  /**
   * List one folder level. Returns items shaped like the storage browser needs:
   * { name, fullPath, type: 'folder' | 'file', size, lastModified }
   */
  async list(container, prefix = '') {
    const query = `container=${encodeURIComponent(container)}&prefix=${encodeURIComponent(prefix)}`;
    const data = await this._request(`/api/storage/list?${query}`);

    return data.items.map(item => ({
      ...item,
      size: item.size || 0,
      lastModified: item.lastModified ? new Date(item.lastModified) : null,
    }));
  }

  async readBlob(container, path) {
    const response = await this._request(this._blobPath(container, path), { raw: true });
    return response.blob();
  }

  async readJson(container, path) {
    const response = await this._request(this._blobPath(container, path), { raw: true });
    return response.json();
  }

  async writeBlob(container, path, blob, contentType = 'application/json') {
    await this._request(this._blobPath(container, path), {
      method: 'PUT',
      body: blob,
      contentType,
    });
  }

  async deleteBlob(container, path) {
    await this._request(this._blobPath(container, path), { method: 'DELETE' });
  }

  /** Read-only SAS URL for a single video, valid for the length of a session. */
  async videoUrl(container, path) {
    const data = await this._request('/api/storage/video-url', {
      method: 'POST',
      body: JSON.stringify({ container, path }),
      contentType: 'application/json',
    });
    return data.url;
  }
}

const storageGatewayService = new StorageGatewayService();
export default storageGatewayService;
